import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Label } from "reactstrap";
import { Plus, Trash2 } from "lucide-react";
import Modal from "../modal";
import { Button } from "../ui/Button";
import { createExchangePackage } from "../../api/packages";
import { getCommerceCapabilities } from "../../api/settings";
import { getCodConfig } from "../../api/billing";
import { COD_AMOUNT_RULE, parseCodAmount } from "../../utils/codAmount";
import { roleRoutes } from "../../utils/roles";
import { showToast } from "../../utils/showToast";

// Diálogo "Crear pedido de cambio": un envío nuevo (hijo) a la misma dirección del
// pedido entregado, que lleva el producto de reemplazo y retira el original. Lo
// crea microservice-packages; acá solo se arma el body.
//
// Regla del body: lo que el usuario no tocó NO se manda, para que el backend lo
// herede completo del padre (los productos con todos sus campos, no solo los tres
// que edita este diálogo). Si se toca algún producto viaja la lista entera, y cada
// fila lleva los campos del padre que siguen valiendo (ver carriedFieldsOf). El
// cobro contra entrega es la excepción: nunca se hereda, así que solo viaja si se
// escribe un monto.
//
// Va sobre el reactstrap Modal del repo (components/modal) y no sobre otro primitivo
// porque se abre DENTRO del modal reactstrap de la etiqueta: reactstrap maneja los
// modales anidados (Esc, Tab y backdrop actúan solo sobre el de arriba).

// Topes del DTO del backend (CreateExchangeDto). Se replican solo para avisar antes
// de enviar; la validación que manda es la de microservice-packages.
const MAX_PRODUCTS = 100;
const MAX_NAME_LENGTH = 200;
const MAX_SKU_LENGTH = 100;
const MAX_QUANTITY = 10000;
const MAX_COMMENTS_LENGTH = 1000;

const COD_OFF = { enabled: false, currency: "" };

const INTEGER_FORMAT = /^\d+$/;

/** Cantidad entera entre 1 y MAX_QUANTITY, como la exige el DTO (@IsInt). */
const isValidQuantity = (raw) => {
  const text = String(raw ?? "").trim();
  if (!INTEGER_FORMAT.test(text)) return false;
  const quantity = Number(text);
  return quantity >= 1 && quantity <= MAX_QUANTITY;
};

/** Monto del DTO: número ≥ 0 con máximo 2 decimales (@IsNumber maxDecimalPlaces 2). */
const isMoneyValue = (value) =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= 0 &&
  Number(value.toFixed(2)) === value;

const isNonNegativeNumber = (value) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isStringUpTo = (max) => (value) => typeof value === "string" && value.length <= max;

// Campos de un producto del padre que acepta ExchangeProductDto además de los tres
// editables, con su regla. El backend hace whitelist (lo no declarado, como
// `dimensions` o `_id`, se descarta), pero un campo declarado con un valor inválido
// es un 400 por algo que el usuario no ve ni puede corregir: ese campo no viaja.
const CARRIED_PRODUCT_FIELDS = {
  unitValue: isMoneyValue,
  totalValue: isMoneyValue,
  weight: isNonNegativeNumber,
  volume: isNonNegativeNumber,
  itemId: isStringUpTo(100),
  currency: isStringUpTo(10),
  image: isStringUpTo(2048),
};

/** Campos editables de un producto del padre, como texto para los inputs. */
const editableFieldsOf = (product) => ({
  name: String(product?.name ?? product?.productName ?? ""),
  sku: String(product?.sku ?? ""),
  quantity: String(product?.quantity ?? 1),
});

const sameIdentity = (row) => {
  const base = editableFieldsOf(row.original);
  return row.name.trim() === base.name.trim() && row.sku.trim() === base.sku.trim();
};

const unchangedRow = (row) =>
  row.original !== null &&
  sameIdentity(row) &&
  Number(row.quantity) === Number(editableFieldsOf(row.original).quantity);

/**
 * Campos del producto del padre que siguen valiendo para la fila: todos si la
 * fila no se tocó; si cambió solo la cantidad, todos menos `totalValue` (quedaría
 * desactualizado); si cambió el producto (nombre o SKU), ninguno, porque para
 * otro producto serían datos inventados.
 */
const carriedFieldsOf = (row) => {
  if (!row.original || !sameIdentity(row)) return {};
  const keepTotal = unchangedRow(row);
  const fields = {};
  Object.entries(CARRIED_PRODUCT_FIELDS).forEach(([field, isValid]) => {
    if (field === "totalValue" && !keepTotal) return;
    if (isValid(row.original[field])) fields[field] = row.original[field];
  });
  return fields;
};

const toProductPayload = (row) => {
  const sku = row.sku.trim();
  return {
    name: row.name.trim(),
    ...(sku && { sku }),
    quantity: Number(row.quantity),
    ...carriedFieldsOf(row),
  };
};

/**
 * ¿El operador cobra contra entrega? Falla cerrado: cualquier respuesta que no
 * diga `enabled: true` (o un error) apaga el campo, igual que el wizard de
 * creación. El comercio no puede leer la config del operador y la recibe del BFF
 * (commerce-capabilities); el back-office lee la suya en billing/cod-config.
 */
const loadCodCapability = async (role) => {
  if (role === roleRoutes.client) {
    const response = await getCommerceCapabilities();
    const body = response?.data;
    const cashOnDelivery = (body?.data ?? body)?.cashOnDelivery;
    return cashOnDelivery?.enabled
      ? { enabled: true, currency: cashOnDelivery.currency || "" }
      : COD_OFF;
  }
  const response = await getCodConfig();
  const body = response?.data;
  const config =
    body && typeof body === "object" && !Array.isArray(body) && "data" in body
      ? body.data
      : body;
  return config?.enabled ? { enabled: true, currency: config.currency || "" } : COD_OFF;
};

/**
 * @param {object} props
 * @param {object} props.parentPackage - pedido entregado (padre)
 * @param {string} props.role - _id del rol del usuario
 * @param {{receiver: string, line: string, municipality: string}} props.address - dirección ya limpia, solo lectura
 * @param {() => void} props.onClose
 * @param {(child: object) => void} props.onCreated
 * @param {(active: {childId: string|null, followingNumber: number|null}) => void} props.onInProgress
 * @param {(label: {returnLabelId: string|null, followingNumber: number|null}) => void} props.onReturnLabelExists
 */
export default function CreateExchangeDialog({
  parentPackage,
  role,
  address,
  onClose,
  onCreated,
  onInProgress,
  onReturnLabelExists,
}) {
  const { t } = useTranslation();
  const parentProducts = Array.isArray(parentPackage?.products) ? parentPackage.products : [];
  const initialComments = String(parentPackage?.comments ?? "");

  const rowKeyRef = useRef(0);
  const newRowKey = () => {
    rowKeyRef.current += 1;
    return `exchange-row-${rowKeyRef.current}`;
  };
  const [rows, setRows] = useState(() =>
    parentProducts.map((product) => ({
      key: newRowKey(),
      ...editableFieldsOf(product),
      original: product,
    })),
  );
  const [comments, setComments] = useState(initialComments);
  const [codAmount, setCodAmount] = useState("");
  const [cod, setCod] = useState(COD_OFF);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // El estado `submitting` recién se ve en el próximo render: dos clics en el
  // mismo tick pasarían los dos. El ref corta el segundo en el acto.
  const inFlightRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    loadCodCapability(role)
      .then((capability) => {
        if (alive) setCod(capability);
      })
      .catch(() => {
        // Sin el dato no se ofrece el campo: un monto que el operador no cobra
        // es peor que no poder indicarlo.
        if (alive) setCod(COD_OFF);
      });
    return () => {
      alive = false;
    };
  }, [role]);

  const close = () => {
    if (inFlightRef.current) return;
    onClose?.();
  };

  const updateRow = (key, field, value) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  const removeRow = (key) => setRows((current) => current.filter((row) => row.key !== key));

  const addRow = () =>
    setRows((current) => [
      ...current,
      { key: newRowKey(), name: "", sku: "", quantity: "1", original: null },
    ]);

  // Sin tocar los productos no viajan (el backend los hereda tal cual): una fila
  // heredada sin nombre o con una cantidad rara no puede bloquear el cambio. Si
  // se tocan, viajan TODAS y el DTO valida todas, así que se avisa antes.
  const productsUnchanged =
    rows.length === parentProducts.length && rows.every((row) => unchangedRow(row));
  const rowErrors = rows.map((row) => ({
    name: !productsUnchanged && !row.name.trim(),
    quantity: !productsUnchanged && !isValidQuantity(row.quantity),
  }));
  const rowsInvalid = rowErrors.some((error) => error.name || error.quantity);
  // Sin filas no se puede mandar `products` (el DTO exige mínimo 1) y omitirlo
  // heredaría los productos del padre que el usuario acaba de quitar.
  const productsMissing = rows.length === 0 && parentProducts.length > 0;
  const codParsed = cod.enabled ? parseCodAmount(codAmount) : { status: "empty" };
  const codInvalid = codParsed.status === "invalid";

  const buildBody = () => {
    const body = {};
    if (!productsUnchanged && rows.length > 0) body.products = rows.map(toProductPayload);
    if (comments.trim() !== initialComments.trim()) body.comments = comments.trim();
    if (codParsed.status === "ok") body.cashOnDelivery = { amount: codParsed.amount };
    return body;
  };

  const handleSubmit = async () => {
    if (inFlightRef.current) return;
    setSubmitted(true);
    setSubmitError(null);
    if (rowsInvalid || productsMissing || codInvalid) return;

    inFlightRef.current = true;
    setSubmitting(true);
    const { success, data, error } = await createExchangePackage(parentPackage._id, buildBody());
    inFlightRef.current = false;
    if (!aliveRef.current) return;
    setSubmitting(false);

    if (success) {
      const number = data?.followingNumber;
      showToast(
        number
          ? t("Cambio #{{number}} creado correctamente.", { number })
          : t("Pedido de cambio creado correctamente."),
        true,
      );
      onCreated?.(data);
      return;
    }

    if (error?.code === "exchange_in_progress") {
      const number = error.followingNumber;
      showToast(
        number
          ? t("Ya hay un cambio en curso (#{{number}}).", { number })
          : t("Ya hay un cambio en curso para este pedido."),
        "warning",
      );
      onInProgress?.({ childId: error.childId, followingNumber: number ?? null });
      return;
    }

    // La etiqueta de devolución y el cambio se excluyen sobre un mismo padre: si ya
    // hay etiqueta, el producto original ya se retira por ahí.
    if (error?.code === "return_label_exists") {
      const number = error.followingNumber;
      showToast(
        number
          ? t("Este pedido ya tiene una etiqueta de devolución (#{{number}}).", { number })
          : t("Este pedido ya tiene una etiqueta de devolución."),
        "warning",
      );
      onReturnLabelExists?.({ returnLabelId: error.returnLabelId, followingNumber: number ?? null });
      return;
    }

    setSubmitError(
      error?.code === "parent_not_delivered"
        ? t("Solo se puede crear un cambio de un pedido entregado, y este pedido ya no está en estado Entregado.")
        : error?.message || t("No se pudo crear el pedido de cambio."),
    );
  };

  const body = (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted mb-0">
        {t("Se crea un envío nuevo a la misma dirección: el repartidor entrega el producto de reemplazo y retira el producto original.")}
      </p>

      <section>
        <h6 className="font-semibold mb-1">{t("Dirección de entrega")}</h6>
        <p className="mb-0">{address?.receiver}</p>
        <p className="mb-0">{address?.line}</p>
        {address?.municipality && <p className="mb-0 text-fg-muted">{address.municipality}</p>}
        <small className="text-muted d-block mt-1">
          {t("Es la dirección del pedido original y no se puede cambiar.")}
        </small>
      </section>

      <section>
        <h6 className="font-semibold mb-2">{t("Productos")}</h6>
        {rows.length === 0 && (
          <p className="text-sm text-fg-muted mb-2">
            {parentProducts.length > 0
              ? t("Agrega al menos un producto.")
              : t("El pedido original no tiene productos registrados. Puedes agregarlos o crear el cambio sin productos.")}
          </p>
        )}
        <div className="flex flex-col gap-2">
          {rows.map((row, index) => {
            const n = index + 1;
            return (
              <div key={row.key} className="flex items-start gap-2">
                <Input
                  aria-label={t("Nombre del producto {{n}}", { n })}
                  placeholder={t("Nombre")}
                  value={row.name}
                  maxLength={MAX_NAME_LENGTH}
                  invalid={submitted && rowErrors[index].name}
                  onChange={(event) => updateRow(row.key, "name", event.target.value)}
                />
                <Input
                  aria-label={t("SKU del producto {{n}}", { n })}
                  placeholder="SKU"
                  value={row.sku}
                  maxLength={MAX_SKU_LENGTH}
                  style={{ maxWidth: "9rem" }}
                  onChange={(event) => updateRow(row.key, "sku", event.target.value)}
                />
                <Input
                  aria-label={t("Cantidad del producto {{n}}", { n })}
                  type="number"
                  min="1"
                  max={MAX_QUANTITY}
                  step="1"
                  value={row.quantity}
                  invalid={submitted && rowErrors[index].quantity}
                  style={{ maxWidth: "6rem" }}
                  onChange={(event) => updateRow(row.key, "quantity", event.target.value)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("Quitar producto {{n}}", { n })}
                  title={t("Quitar producto {{n}}", { n })}
                  onClick={() => removeRow(row.key)}
                  disabled={submitting}
                >
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              </div>
            );
          })}
        </div>
        {submitted && rowsInvalid && (
          <small className="text-danger d-block mt-1">
            {t("Cada producto necesita un nombre y una cantidad entera entre 1 y {{max}}.", { max: MAX_QUANTITY })}
          </small>
        )}
        {submitted && productsMissing && (
          <small className="text-danger d-block mt-1">{t("Agrega al menos un producto.")}</small>
        )}
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          iconLeft={<Plus size={14} aria-hidden="true" />}
          onClick={addRow}
          disabled={submitting || rows.length >= MAX_PRODUCTS}
        >
          {t("Agregar producto")}
        </Button>
      </section>

      <section>
        <Label htmlFor="exchange-comments" className="font-semibold">{t("Comentario")}</Label>
        <Input
          id="exchange-comments"
          type="textarea"
          rows={3}
          maxLength={MAX_COMMENTS_LENGTH}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
        />
      </section>

      {cod.enabled && (
        <section>
          <Label htmlFor="exchange-cod-amount" className="font-semibold">
            {t("Monto a cobrar")}{cod.currency ? ` (${cod.currency})` : ""}
          </Label>
          <Input
            id="exchange-cod-amount"
            type="number"
            min="0"
            step="0.01"
            placeholder={t("Ej: 19990")}
            value={codAmount}
            invalid={submitted && codInvalid}
            onChange={(event) => setCodAmount(event.target.value)}
          />
          {submitted && codInvalid && (
            <small className="text-danger d-block mt-1">{t(COD_AMOUNT_RULE)}</small>
          )}
          <small className="text-muted d-block mt-1">
            {t("El cambio no hereda el cobro del pedido original. Déjalo vacío si no se cobra al destinatario.")}
          </small>
        </section>
      )}

      {submitError && (
        <div className="alert alert-danger mb-0" role="alert">
          {submitError}
        </div>
      )}
    </div>
  );

  const footer = (
    <>
      <Button variant="outline" onClick={close} disabled={submitting}>
        {t("Cancelar")}
      </Button>
      <Button variant="primary" onClick={handleSubmit} loading={submitting}>
        {submitting ? t("Creando...") : t("Crear cambio")}
      </Button>
    </>
  );

  return (
    <Modal
      open
      setOpen={close}
      centered
      size="lg"
      header={
        <>
          {t("Crear pedido de cambio")}{" "}
          <span className="text-fg-muted font-normal">
            · {t("Pedido #{{number}}", { number: parentPackage?.followingNumber })}
          </span>
        </>
      }
      body={body}
      footer={footer}
    />
  );
}
